import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabaseClient';

/** 막대·서술 표시 순서(코호트에 없는 항목은 제외) */
const REPORT_CODE_ORDER = ['psqi', 'isi', 'berlin', 'oabss'];

/* ───────────────────────── 질문지 아이콘 ───────────────────────── */
const IconMoon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
const IconPulse = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);
const IconWind = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
  </svg>
);
const IconDrop = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);

const Score = ({ children, tone = 'pre' }) => (
  <span className={`db-crx-score db-crx-score--${tone}`}>{children}</span>
);

/**
 * 질문지별 메타데이터
 * - icon / title / description : 카드 헤더·설명
 * - max : 막대 그래프 최대 눈금
 * - get : reportData에서 { pre, post } 추출
 * - interpret(pre) : 사전 점수 해석 → { tone: 'good' | 'warn', node }
 * - change : 사후 리포트 변화 보고용 { label, unit }
 */
const INSTRUMENT_CONFIG = {
  psqi: {
    icon: <IconMoon />,
    title: '피츠버그 수면의 질 설문(PSQI)',
    descriptionPre:
      '다음은 귀하가 주관적으로 느낀 수면의 질을 질문지로 평가한 결과입니다. 질문지의 점수가 높을수록 수면의 질이 낮음을 의미합니다.',
    description:
      '다음은 프로그램 실시 전과 실시 이후, 주관적으로 느낀 수면의 질을 질문지로 평가한 결과입니다. 질문지의 점수가 높을수록 수면의 질이 낮음을 의미합니다.',
    max: 21,
    get: (d) => d.psqi,
    interpret: (pre, showPost) =>
      pre < 6
        ? {
            tone: 'good',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 수면의 질 점수는{' '}
                <Score>{pre}점</Score>
                {showPost ? '이었습니다' : '입니다'}. 이는 귀하의 수면의 질이 매우 양호한 상태임을
                반영합니다.
              </>
            ),
          }
        : {
            tone: 'warn',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 수면의 질 점수는{' '}
                <Score>{pre}점</Score>
                {showPost ? '이었습니다' : '입니다'}. 이는 귀하의 수면의 질이 다소 낮음을
                반영합니다.
              </>
            ),
          },
    change: { name: 'PSQI 점수', unit: '점' },
  },
  isi: {
    icon: <IconPulse />,
    title: '불면증 심각도 설문지(ISI)',
    descriptionPre:
      '다음은 귀하가 주관적으로 느낀 수면의 어려움을 질문지로 평가한 결과입니다. 질문지의 점수가 높을수록 수면의 어려움이 심각함을 의미합니다.',
    description:
      '다음은 수면 코칭 실시 전과 실시 이후, 주관적으로 느낀 수면의 어려움을 질문지로 평가한 결과입니다. 질문지의 점수가 높을수록 수면의 어려움이 심각함을 의미합니다.',
    max: 28,
    get: (d) => d.isi,
    interpret: (pre, showPost) =>
      pre < 15
        ? {
            tone: 'good',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 불면증 심각도 점수는{' '}
                <Score>{pre}점</Score>
                {showPost ? '이었습니다' : '입니다'}. 이는 귀하가 느끼는 수면의 어려움이 거의 없음을
                반영합니다.
              </>
            ),
          }
        : {
            tone: 'warn',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 불면증 심각도 점수는{' '}
                <Score>{pre}점</Score>
                {showPost ? '이었습니다' : '입니다'}. 이는 귀하가 수면을 취하는데 상당한 어려움과
                불편을 경험하고 있음을 반영합니다.
              </>
            ),
          },
    change: { name: 'ISI 점수', unit: '점' },
  },
  berlin: {
    icon: <IconWind />,
    title: '베를린 질문지(Berlin)',
    description:
      '다음은 수면 코칭 실시 전과 실시 이후, 수면 중 코골이나 호흡으로 인한 불편감을 질문지로 평가한 결과입니다. 3개 영역 중 2개 이상이 기준점보다 높으면 수면 중 호흡 불편감이 클 수 있습니다.',
    max: 3,
    get: (d) => d.berlinPositiveCount,
    interpret: (pre) =>
      pre < 2
        ? {
            tone: 'good',
            node: (
              <>
                총 3개 영역 중 사전(1주차)에 양성 판정된 영역이 <Score>{pre}개</Score>입니다. 이는
                현재 귀하가 느끼는 불편감이 거의 없음을 반영합니다.
              </>
            ),
          }
        : {
            tone: 'warn',
            node: (
              <>
                총 3개 영역 중 사전(1주차)에 양성 판정된 영역이 <Score>{pre}개</Score>입니다. 이는
                현재 귀하가 코골이 또는 수면 중 호흡으로 인해 상당한 불편을 경험하고 있음을
                반영합니다.
              </>
            ),
          },
    change: { name: 'Berlin 양성 판정 영역 수', unit: '개' },
  },
  oabss: {
    icon: <IconDrop />,
    title: '과민성방광증상 질문지(OABSS)',
    description:
      '다음은 수면 코칭 실시 전과 실시 이후, 밤 중 화장실 문제로 인한 불편감을 질문지로 평가한 결과입니다. 질문지의 점수가 높을수록 불편감이 큼을 의미합니다.',
    max: 15,
    get: (d) => d.oabss,
    interpret: (pre, showPost) =>
      pre < 7
        ? {
            tone: 'good',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 과민성방광증상 점수는{' '}
                <Score>{pre}점</Score>입니다. 이는 귀하가 밤 중 화장실 문제로 불편감을 거의 경험하지
                않음을 반영합니다.
              </>
            ),
          }
        : {
            tone: 'warn',
            node: (
              <>
                {showPost ? '프로그램 실시 전' : '현재'} 귀하의 과민성방광증상 점수는{' '}
                <Score>{pre}점</Score>입니다. 이는 귀하가 밤 중 화장실 문제로 상당한 어려움과 불편을
                경험하고 있음을 반영합니다.
              </>
            ),
          },
    change: { name: 'OABSS 점수', unit: '점' },
  },
};

/** 가로형 막대 한 줄 (사전/사후) */
function CardBar({ tag, value, max, variant }) {
  const hasValue = typeof value === 'number';
  const pct = hasValue && max > 0 ? Math.min(100, (value / max) * 100) : 0;
  // 점수 숫자가 항상 보이도록 채움 막대에 최소 너비 부여
  const width = hasValue ? Math.max(pct, value > 0 ? 13 : 0) : 0;
  return (
    <div className="db-crx-bar-row">
      <span className="db-crx-bar-tag">{tag}</span>
      <div
        className="db-crx-bar-track"
        role="img"
        aria-label={`${tag} ${hasValue ? value : '기록 없음'}`}
      >
        {hasValue ? (
          <div
            className={`db-crx-bar-fill db-crx-bar-fill--${variant}`}
            style={{ width: `${width}%` }}
          >
            <span className="db-crx-bar-val">{value}</span>
          </div>
        ) : (
          <span className="db-crx-bar-empty">기록 없음</span>
        )}
      </div>
    </div>
  );
}

/** 받침 유무에 따라 '으로 / 로' 조사 선택 (받침 없음·ㄹ받침 → '로') */
function euroParticle(word) {
  const last = word.charCodeAt(word.length - 1);
  if (last < 0xac00 || last > 0xd7a3) return '로';
  const jong = (last - 0xac00) % 28;
  return jong === 0 || jong === 8 ? '로' : '으로';
}

/** 사후 리포트: 사전-사후 변화 한 줄 서술 (사후 점수는 초록색으로 강조) */
function changeSentence(meta, pre, post) {
  const { name, unit } = meta;
  const p = euroParticle(unit);
  if (post > pre) {
    return (
      <>
        프로그램 실시 후 {name}는{' '}
        <Score tone="post">
          {post}
          {unit}
        </Score>
        {p} 이전에 비해 {post - pre}
        {unit} 증가하였습니다.
      </>
    );
  }
  if (post < pre) {
    return (
      <>
        프로그램 실시 후 {name}는{' '}
        <Score tone="post">
          {post}
          {unit}
        </Score>
        {p} 이전에 비해 {pre - post}
        {unit} 감소하였습니다.
      </>
    );
  }
  return (
    <>
      프로그램 실시 후 {name} 또한{' '}
      <Score tone="post">
        {post}
        {unit}
      </Score>
      {p} 사전-사후 모두 일정하게 유지되고 있음을 확인할 수 있습니다.
    </>
  );
}

export function DashboardChangeReportModal({
  open,
  onClose,
  userId,
  cohortCareOptionId,
  phase = 'pre',
}) {
  const [reportData, setReportData] = useState(null);
  const [activeInstrumentCodes, setActiveInstrumentCodes] = useState(REPORT_CODE_ORDER);
  const [loading, setLoading] = useState(false);
  const showPost = phase === 'post';

  useEffect(() => {
    if (!open || !userId || !cohortCareOptionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setReportData(null);

      let activeCodes = REPORT_CODE_ORDER;

      const { data: csi, error: csiErr } = await supabase
        .from('cohort_survey_instruments')
        .select('display_order, instrument:survey_instruments!inner(id, code, is_active)')
        .eq('cohort_care_option_id', cohortCareOptionId)
        .order('display_order', { ascending: true });
      if (cancelled) {
        setLoading(false);
        return;
      }
      if (csiErr) {
        console.error('[changeReport cohort_survey_instruments]', csiErr);
      } else {
        let codes = (csi || [])
          .map((r) => r.instrument)
          .filter((i) => i && i.is_active !== false && i.code)
          .map((i) => i.code);
        codes = codes.filter((c) => REPORT_CODE_ORDER.includes(c));
        const seen = new Set();
        codes = codes.filter((c) => (seen.has(c) ? false : (seen.add(c), true)));
        if (codes.length === 0) {
          const { data: activeRows, error: actErr } = await supabase
            .from('survey_instruments')
            .select('code')
            .eq('is_active', true);
          if (cancelled) {
            setLoading(false);
            return;
          }
          if (actErr) {
            console.error('[changeReport survey_instruments active]', actErr);
            codes = [...REPORT_CODE_ORDER];
          } else {
            const have = new Set((activeRows || []).map((r) => r.code).filter(Boolean));
            codes = REPORT_CODE_ORDER.filter((c) => have.has(c));
            if (codes.length === 0) codes = [...REPORT_CODE_ORDER];
          }
        }
        activeCodes = codes;
      }

      const { data: submissions, error: subErr } = await supabase
        .from('survey_submissions')
        .select('id, phase, instrument:survey_instruments!inner(code)')
        .eq('user_id', userId)
        .eq('cohort_care_option_id', cohortCareOptionId);
      if (cancelled) {
        setLoading(false);
        return;
      }
      if (subErr) {
        setLoading(false);
        if (!cancelled) {
          setActiveInstrumentCodes(activeCodes);
          setReportData(null);
        }
        console.error('[changeReport survey_submissions]', subErr);
        return;
      }
      const ids = (submissions || []).map((s) => s.id).filter(Boolean);
      if (!ids.length) {
        setLoading(false);
        if (!cancelled) {
          setActiveInstrumentCodes(activeCodes);
          setReportData(null);
        }
        return;
      }
      const { data: results, error: resErr } = await supabase
        .from('survey_submission_results')
        .select('submission_id, psqi_global, isi_total, berlin_positive_region_count, oabss_sum')
        .in('submission_id', ids);
      if (cancelled) {
        setLoading(false);
        return;
      }
      setLoading(false);
      if (resErr) {
        if (!cancelled) {
          setActiveInstrumentCodes(activeCodes);
          setReportData(null);
        }
        console.error('[changeReport survey_submission_results]', resErr);
        return;
      }

      const resultBySubmissionId = new Map((results || []).map((r) => [r.submission_id, r]));
      const d = {
        psqi: { pre: null, post: null },
        isi: { pre: null, post: null },
        berlinPositiveCount: { pre: null, post: null },
        oabss: { pre: null, post: null },
      };

      (submissions || []).forEach((s) => {
        const code = s?.instrument?.code;
        const phase = s?.phase;
        const r = resultBySubmissionId.get(s.id);
        if (!code || !phase || !r) return;
        if (code === 'psqi') d.psqi[phase] = r.psqi_global;
        if (code === 'isi') d.isi[phase] = r.isi_total;
        if (code === 'berlin') d.berlinPositiveCount[phase] = r.berlin_positive_region_count;
        if (code === 'oabss') d.oabss[phase] = r.oabss_sum;
      });
      if (!cancelled) {
        setActiveInstrumentCodes(activeCodes);
        setReportData(d);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, cohortCareOptionId]);

  if (!open) return null;
  const d = reportData;

  return (
    <div className="db-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="db-modal db-modal--change-report"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="db-change-report-title"
      >
        <div className="db-change-report-header">
          <h2 id="db-change-report-title" className="db-change-report-title">
            {showPost ? '변화 리포트' : '사전 검사 결과'}
          </h2>
          <button
            type="button"
            className="db-change-report-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div className="db-change-report-scroll">
          {loading && (
            <p
              className="db-change-report-p db-change-report-p--loading"
              role="status"
              aria-live="polite"
            >
              <span className="db-spinner" aria-hidden />
            </p>
          )}
          {!loading && !d && (
            <p className="db-change-report-p">
              아직 리포트 데이터가 없습니다. 사전/사후 설문 제출 및 결과 계산 후 다시 확인해 주세요.
            </p>
          )}
          {!loading && d && (
            <div className="db-crx-list">
              {activeInstrumentCodes.map((code, idx) => {
                const cfg = INSTRUMENT_CONFIG[code];
                if (!cfg) return null;
                const { pre, post } = cfg.get(d);
                const interp = typeof pre === 'number' ? cfg.interpret(pre, showPost) : null;
                const hasChange = showPost && typeof pre === 'number' && typeof post === 'number';
                return (
                  <article
                    className="db-crx-card"
                    key={code}
                    style={{ animationDelay: `${idx * 70}ms` }}
                  >
                    <header className="db-crx-card-head">
                      <span className="db-crx-icon">{cfg.icon}</span>
                      <h3 className="db-crx-card-title">{cfg.title}</h3>
                    </header>

                    <p className="db-crx-desc">
                      {showPost ? cfg.description : cfg.descriptionPre || cfg.description}
                    </p>

                    <div className="db-crx-bars">
                      <CardBar tag="사전" value={pre} max={cfg.max} variant="pre" />
                      {showPost && <CardBar tag="사후" value={post} max={cfg.max} variant="post" />}
                    </div>

                    {(interp || hasChange) && (
                      <div className={`db-crx-note db-crx-note--${interp ? interp.tone : 'good'}`}>
                        {interp && <p className="db-crx-note-line">{interp.node}</p>}
                        {hasChange && (
                          <p className="db-crx-note-line db-crx-note-change">
                            {changeSentence(cfg.change, pre, post)}
                          </p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}

              <p className="db-crx-disclaimer" role="note">
                <strong>※ 안내</strong> 해당 검사는 임상적 진단을 내리기 위해 개발된 검사 도구가
                아니며, 귀하의 전반적인 상태만을 반영하므로, 정확한 진단을 위해서는 의료 전문가의
                검사 및 진단을 받아보시기를 권장드립니다.
              </p>
            </div>
          )}
        </div>

        <div className="db-modal-actions db-change-report-actions">
          <button type="button" className="db-btn db-btn--primary db-btn--block" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}
